import React, { useEffect, useMemo, useState } from 'react'
import './RelatedCategoryPicker.sass'
import PopUp from '../../../ui/PopUp/PopUp'
import Icon from '../../../ui/Icon/Icon'
import Spinner from '../../../ui/Spinner/Spinner'
import loadRelatedCategoryTree from '../../../api/marketing/loadRelatedCategoryTree'
import { isOutletBranchActive, OUTLET_ROOT_NAME } from '../../../api/marketing/outlet/outletCategoryConstants'
import buildRelatedProductFilter from '../../../api/marketing/buildRelatedProductFilter'
import { createCategorySelection, isCategorySelectionValid } from '../../../api/marketing/relatedCategorySelection'

function CategoryPickerPopUp({ tree, isLoading, loadError, onClose, onSelect }) {
  const [activeGlobalId, setActiveGlobalId] = useState(null)
  const [activeOutletGlobalId, setActiveOutletGlobalId] = useState(null)
  const [activeCategoryName, setActiveCategoryName] = useState(null)

  const activeGlobal = useMemo(
    () => tree.find((node) => node.id === activeGlobalId) || tree[0] || null,
    [tree, activeGlobalId]
  )

  const isOutletMode = isOutletBranchActive(activeGlobal)

  const activeOutletGlobal = useMemo(() => {
    if (!isOutletMode) {
      return null
    }

    return (
      activeGlobal?.children?.find((node) => node.id === activeOutletGlobalId) ||
      activeGlobal?.children?.[0] ||
      null
    )
  }, [activeGlobal, activeOutletGlobalId, isOutletMode])

  const columnTwoNodes = isOutletMode ? activeGlobal?.children || [] : activeGlobal?.children || []

  const columnTwoParent = isOutletMode ? activeGlobal : activeGlobal

  const activeCategoryParent = isOutletMode ? activeOutletGlobal : activeGlobal

  const activeCategory = useMemo(
    () =>
      activeCategoryParent?.children?.find((node) => node.name === activeCategoryName) ||
      activeCategoryParent?.children?.[0] ||
      null,
    [activeCategoryParent, activeCategoryName]
  )

  useEffect(() => {
    if (tree.length === 0) {
      setActiveGlobalId(null)
      setActiveOutletGlobalId(null)
      setActiveCategoryName(null)
      return
    }

    setActiveGlobalId((currentId) => {
      if (currentId && tree.some((node) => node.id === currentId)) {
        return currentId
      }

      return tree[0].id
    })
  }, [tree])

  useEffect(() => {
    if (!isOutletMode) {
      setActiveOutletGlobalId(null)
    } else if (activeGlobal?.children?.length) {
      setActiveOutletGlobalId((currentId) => {
        if (currentId && activeGlobal.children.some((node) => node.id === currentId)) {
          return currentId
        }

        return activeGlobal.children[0].id
      })
    } else {
      setActiveOutletGlobalId(null)
    }
  }, [activeGlobal, isOutletMode])

  useEffect(() => {
    if (!activeCategoryParent?.children?.length) {
      setActiveCategoryName(null)
      return
    }

    setActiveCategoryName((currentName) => {
      if (currentName && activeCategoryParent.children.some((node) => node.name === currentName)) {
        return currentName
      }

      return activeCategoryParent.children[0].name
    })
  }, [activeCategoryParent])

  const handleSelectPremiumGlobal = (globalCategory) => {
    onSelect(
      createCategorySelection({
        source: 'premium',
        level: 'global',
        globalCategory,
        path: [globalCategory.name],
        brandIds: globalCategory.brandIds,
        productFilter: buildRelatedProductFilter({
          source: 'premium',
          level: 'global',
          globalNode: globalCategory,
        }),
      })
    )
    onClose()
  }

  const handleSelectOutletRoot = (outletRoot) => {
    onSelect(
      createCategorySelection({
        source: 'outlet',
        level: 'outlet_root',
        globalCategory: outletRoot,
        path: [OUTLET_ROOT_NAME],
        brandIds: outletRoot.brandIds,
        productFilter: buildRelatedProductFilter({
          source: 'outlet',
          level: 'outlet_root',
        }),
      })
    )
    onClose()
  }

  const handleSelectOutletGlobal = (outletRoot, outletGlobal) => {
    onSelect(
      createCategorySelection({
        source: 'outlet',
        level: 'global',
        globalCategory: outletRoot,
        outletGlobalCategoryId: outletGlobal.globalCategoryId,
        path: [OUTLET_ROOT_NAME, outletGlobal.name],
        brandIds: outletGlobal.brandIds,
        productFilter: buildRelatedProductFilter({
          source: 'outlet',
          level: 'global',
          outletGlobalNode: outletGlobal,
        }),
      })
    )
    onClose()
  }

  const handleSelectCategory = (globalCategory, categoryNode, options = {}) => {
    const { source = 'premium', outletRoot = null, outletGlobal = null } = options

    if (source === 'outlet' && outletRoot && outletGlobal) {
      onSelect(
        createCategorySelection({
          source: 'outlet',
          level: 'category',
          globalCategory: outletRoot,
          outletGlobalCategoryId: outletGlobal.globalCategoryId,
          path: [OUTLET_ROOT_NAME, outletGlobal.name, categoryNode.name],
          brandIds: categoryNode.brandIds,
          productFilter: buildRelatedProductFilter({
            source: 'outlet',
            level: 'category',
            categoryNode,
          }),
        })
      )
    } else {
      onSelect(
        createCategorySelection({
          source: 'premium',
          level: 'category',
          globalCategory,
          path: [globalCategory.name, categoryNode.name],
          brandIds: categoryNode.brandIds,
          productFilter: buildRelatedProductFilter({
            source: 'premium',
            level: 'category',
            categoryNode,
          }),
        })
      )
    }

    onClose()
  }

  const handleSelectSubcategory = (globalCategory, categoryNode, subcategoryNode, options = {}) => {
    const { source = 'premium', outletRoot = null, outletGlobal = null } = options

    if (source === 'outlet' && outletRoot && outletGlobal) {
      onSelect(
        createCategorySelection({
          source: 'outlet',
          level: 'subcategory',
          globalCategory: outletRoot,
          outletGlobalCategoryId: outletGlobal.globalCategoryId,
          path: [OUTLET_ROOT_NAME, outletGlobal.name, categoryNode.name, subcategoryNode.name],
          brandIds: subcategoryNode.brandIds,
          productFilter: buildRelatedProductFilter({
            source: 'outlet',
            level: 'subcategory',
            subcategoryNode,
          }),
        })
      )
    } else {
      onSelect(
        createCategorySelection({
          source: 'premium',
          level: 'subcategory',
          globalCategory,
          path: [globalCategory.name, categoryNode.name, subcategoryNode.name],
          brandIds: subcategoryNode.brandIds,
          productFilter: buildRelatedProductFilter({
            source: 'premium',
            level: 'subcategory',
            subcategoryNode,
          }),
        })
      )
    }

    onClose()
  }

  const columnTwoTitle = isOutletMode ? 'Глобальні outlet' : 'Категорії'
  const columnThreeTitle = isOutletMode ? 'Категорії' : 'Підкатегорії'

  return (
    <PopUp show title="Оберіть категорію" close={onClose} className="CategoryPickerPopUp">
      {isLoading ? (
        <Spinner type="popup" />
      ) : loadError ? (
        <p className="CategoryPickerPopUp-Message CategoryPickerPopUp-Message_error">{loadError}</p>
      ) : tree.length === 0 ? (
        <p className="CategoryPickerPopUp-Message">Немає категорій для обраних брендів</p>
      ) : (
        <div className="CategoryPickerPopUp-Columns">
          <div className="CategoryPickerPopUp-Column">
            <p className="CategoryPickerPopUp-ColumnTitle">Глобальні</p>
            <ul className="CategoryPickerPopUp-List">
              {tree.map((globalCategory) => (
                <li key={globalCategory.id}>
                  <button
                    type="button"
                    className={[
                      'CategoryPickerPopUp-Item',
                      activeGlobal?.id === globalCategory.id ? 'CategoryPickerPopUp-Item_active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setActiveGlobalId(globalCategory.id)}
                    onFocus={() => setActiveGlobalId(globalCategory.id)}
                    onClick={() =>
                      isOutletBranchActive(globalCategory)
                        ? handleSelectOutletRoot(globalCategory)
                        : handleSelectPremiumGlobal(globalCategory)
                    }
                  >
                    {globalCategory.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="CategoryPickerPopUp-Column">
            <p className="CategoryPickerPopUp-ColumnTitle">{columnTwoTitle}</p>
            {columnTwoNodes.length ? (
              <ul className="CategoryPickerPopUp-List">
                {columnTwoNodes.map((node) => (
                  <li key={isOutletMode ? node.id : node.name}>
                    <button
                      type="button"
                      className={[
                        'CategoryPickerPopUp-Item',
                        isOutletMode
                          ? activeOutletGlobal?.id === node.id
                            ? 'CategoryPickerPopUp-Item_active'
                            : ''
                          : activeCategory?.name === node.name
                            ? 'CategoryPickerPopUp-Item_active'
                            : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseEnter={() => {
                        if (isOutletMode) {
                          setActiveGlobalId(columnTwoParent.id)
                          setActiveOutletGlobalId(node.id)
                        } else {
                          setActiveGlobalId(columnTwoParent.id)
                          setActiveCategoryName(node.name)
                        }
                      }}
                      onFocus={() => {
                        if (isOutletMode) {
                          setActiveGlobalId(columnTwoParent.id)
                          setActiveOutletGlobalId(node.id)
                        } else {
                          setActiveGlobalId(columnTwoParent.id)
                          setActiveCategoryName(node.name)
                        }
                      }}
                      onClick={() =>
                        isOutletMode
                          ? handleSelectOutletGlobal(activeGlobal, node)
                          : handleSelectCategory(activeGlobal, node)
                      }
                    >
                      {node.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="CategoryPickerPopUp-Empty">
                {isOutletMode ? 'Немає outlet-категорій' : 'Немає категорій'}
              </p>
            )}
          </div>

          <div className="CategoryPickerPopUp-Column">
            <p className="CategoryPickerPopUp-ColumnTitle">{columnThreeTitle}</p>
            {isOutletMode ? (
              activeCategoryParent?.children?.length ? (
                <div className="CategoryPickerPopUp-OutletColumn">
                  <ul className="CategoryPickerPopUp-List">
                    {activeCategoryParent.children.map((categoryNode) => (
                      <li key={categoryNode.name}>
                        <button
                          type="button"
                          className={[
                            'CategoryPickerPopUp-Item',
                            activeCategory?.name === categoryNode.name ? 'CategoryPickerPopUp-Item_active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onMouseEnter={() => setActiveCategoryName(categoryNode.name)}
                          onFocus={() => setActiveCategoryName(categoryNode.name)}
                          onClick={() =>
                            handleSelectCategory(activeGlobal, categoryNode, {
                              source: 'outlet',
                              outletRoot: activeGlobal,
                              outletGlobal: activeOutletGlobal,
                            })
                          }
                        >
                          {categoryNode.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {activeCategory?.children?.length ? (
                    <>
                      <p className="CategoryPickerPopUp-SubsectionTitle">Підкатегорії</p>
                      <ul className="CategoryPickerPopUp-List">
                        {activeCategory.children.map((subcategoryNode) => (
                          <li key={subcategoryNode.name}>
                            <button
                              type="button"
                              className="CategoryPickerPopUp-Item"
                              onClick={() =>
                                handleSelectSubcategory(activeGlobal, activeCategory, subcategoryNode, {
                                  source: 'outlet',
                                  outletRoot: activeGlobal,
                                  outletGlobal: activeOutletGlobal,
                                })
                              }
                            >
                              {subcategoryNode.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              ) : (
                <p className="CategoryPickerPopUp-Empty">Немає категорій</p>
              )
            ) : activeCategory?.children?.length ? (
              <ul className="CategoryPickerPopUp-List">
                {activeCategory.children.map((subcategoryNode) => (
                  <li key={subcategoryNode.name}>
                    <button
                      type="button"
                      className="CategoryPickerPopUp-Item"
                      onClick={() =>
                        handleSelectSubcategory(activeGlobal, activeCategory, subcategoryNode)
                      }
                    >
                      {subcategoryNode.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="CategoryPickerPopUp-Empty">Немає підкатегорій</p>
            )}
          </div>
        </div>
      )}
    </PopUp>
  )
}

function RelatedCategoryPicker({
  value = null,
  onChange,
  brandIds = [],
  onBrandsSuggestion,
  invalidMessage = '',
  label = 'Повʼязана категорія',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [tree, setTree] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const hasValue = Boolean(value?.related_category_path_label)
  const pathLabel = value?.related_category_path_label || ''

  useEffect(() => {
    let isMounted = true

    setIsLoading(true)
    setLoadError('')

    loadRelatedCategoryTree({ brandIds })
      .then((nextTree) => {
        if (!isMounted) {
          return
        }

        setTree(nextTree)

        if (value && !isCategorySelectionValid(value, nextTree) && !value.related_category_path_label) {
          onChange(null)
        }
      })
      .catch((error) => {
        console.error('[RelatedCategoryPicker] Failed to load categories:', error)

        if (!isMounted) {
          return
        }

        setLoadError('Не вдалося завантажити категорії')
        setTree([])
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
    // Reload when brand filter changes; value/onChange only used for invalidation side-effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandIds])

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClear = (event) => {
    event.stopPropagation()
    onChange(null)
  }

  const handleSelect = (selection) => {
    onChange(selection)

    if (onBrandsSuggestion) {
      onBrandsSuggestion(selection?.brandIds || [])
    }
  }

  return (
    <div className="UtmBuilder-RelatedCategoryField">
      <div
        className={['Field', 'UtmBuilder-RelatedCategoryControl', hasValue ? 'Field_with_value' : ''].join(' ')}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleOpen()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          type="text"
          className="UtmBuilder-RelatedCategoryInput"
          value={pathLabel}
          readOnly
          placeholder=""
        />
        {hasValue ? (
          <button
            type="button"
            className="UtmBuilder-RelatedCategoryClear"
            title="Очистити"
            onClick={handleClear}
          >
            <Icon name="times" weight="regular" size={12} />
          </button>
        ) : null}
        <span className="Field-Icon Field-Icon_position_right UtmBuilder-RelatedCategoryAngle">
          <Icon name="angle-down" />
        </span>
        <span className="Field-Bar" />
        <label>{label}</label>
      </div>

      {invalidMessage ? (
        <p className="UtmBuilder-RelatedCategoryHint UtmBuilder-RelatedCategoryHint_error">{invalidMessage}</p>
      ) : null}

      {isOpen ? (
        <CategoryPickerPopUp
          tree={tree}
          isLoading={isLoading}
          loadError={loadError}
          onClose={() => setIsOpen(false)}
          onSelect={handleSelect}
        />
      ) : null}
    </div>
  )
}

export default RelatedCategoryPicker
